import { create } from 'zustand'
import { setAutoFreeze } from 'immer'
import { immer } from 'zustand/middleware/immer'
import { devtools } from 'zustand/middleware'
import { isDevtoolsEnabled } from 'src/store/utils/isDevtoolsEnabled'
import type { Notification, NotificationItem } from './types/domain'
import { NOTIFICATION_CONFIG } from './config'

setAutoFreeze(false)

interface NotificationState {
  notifications: Notification[]
}

export interface NotificationActions {
  addNotification: (item: NotificationItem, sticky?: boolean) => string
  removeNotification: (id: string) => void
  hideNotification: (id: string) => void
  reset: () => void
}

type NotificationStore = NotificationState & {
  actions: NotificationActions
}

export const useNotificationStore = create<NotificationStore>()(
  devtools(
    immer((set) => ({
      notifications: [],
      actions: {
        addNotification: (item, sticky = false) => {
          const duration = sticky ? Infinity : NOTIFICATION_CONFIG.DEFAULT_DURATION_MS
          const notification: Notification = {
            id: `${item.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            item,
            visible: true,
            createdAt: Date.now(),
            duration
          }

          set(
            (state) => {
              state.notifications.push(notification)
            },
            undefined,
            'addNotification'
          )

          return notification.id
        },
        removeNotification: (id) => {
          set(
            (state) => {
              state.notifications = state.notifications.filter((n) => n.id !== id)
            },
            undefined,
            'removeNotification'
          )
        },
        hideNotification: (id) => {
          set(
            (state) => {
              const notification = state.notifications.find((n) => n.id === id)
              if (notification) {
                notification.visible = false
              }
            },
            undefined,
            'hideNotification'
          )
        },
        reset: () => {
          set(
            (state) => {
              state.notifications = []
            },
            undefined,
            'resetNotifications'
          )
        }
      }
    })),
    { name: 'NotificationStore', enabled: isDevtoolsEnabled() }
  )
)

export const useNotificationActions = () => useNotificationStore((state) => state.actions)

export const useActiveNotifications = () => useNotificationStore((state) => state.notifications)
